/**
 * ProactiveWelcome — Replaces the blank Skerpa chat state with 2-3 contextual
 * guide cards based on project state. Clicking a card sends a prompt to Skerpa.
 */

import { cn } from '@/lib/utils'
import { useProactiveGuide } from '@/hooks/useProactiveGuide'
import type { GuideCard, GuideDomain } from '@ispot/shared'

const DOMAIN_ACCENT: Record<GuideDomain, string> = {
  market: 'border-l-market',
  business: 'border-l-business',
  solution: 'border-l-solution',
  general: 'border-l-warm-300',
}

interface ProactiveWelcomeProps {
  onSend: (text: string) => void
}

export function ProactiveWelcome({ onSend }: ProactiveWelcomeProps) {
  const { cards, isReady } = useProactiveGuide()

  // Loading skeleton
  if (!isReady) {
    return (
      <div className="flex flex-col h-full px-3 pt-10">
        <div className="space-y-1">
          <div className="h-4 w-24 rounded bg-warm-100 animate-pulse" />
          <div className="h-3 w-40 rounded bg-warm-100/60 animate-pulse" />
        </div>
        <div className="space-y-1 mt-5">
          {[1, 2].map(i => (
            <div key={i} className="h-14 rounded bg-warm-100/40 animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  // No cards — fall back to original empty state
  if (cards.length === 0) {
    return (
      <div className="flex flex-col h-full px-3 pt-10">
        <p className="text-sm font-semibold text-text font-display">What's next?</p>
        <p className="text-xs text-text-muted mt-0.5 leading-relaxed">
          Ask about PM frameworks, research markets, analyze competitors, or get product strategy advice.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full px-3">
      {/* Header */}
      <div className="pt-10 pb-3">
        <p className="text-sm font-semibold text-text font-display">What's next?</p>
        <p className="text-[11px] text-text-muted mt-0.5">Based on your project progress</p>
      </div>

      {/* Guide cards */}
      <div className="space-y-1">
        {cards.map(card => (
          <GuideCardButton key={card.id} card={card} onClick={() => onSend(card.prompt)} />
        ))}
      </div>

      {/* Hint */}
      <div className="mt-auto pb-2 pt-4">
        <p className="text-[10px] text-text-muted">or type any question below</p>
      </div>
    </div>
  )
}

// ── Card component ──

function GuideCardButton({ card, onClick }: { card: GuideCard; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left border-l-2 pl-3 pr-2 py-2 transition-colors duration-100',
        'hover:bg-warm-50 rounded-r-md',
        DOMAIN_ACCENT[card.domain],
      )}
    >
      <p className="text-[13px] font-semibold text-text leading-snug">{card.title}</p>
      <p className="text-[11px] text-text-muted mt-0.5 leading-relaxed">{card.description}</p>
    </button>
  )
}
